import Service from '@ember/service';
import { service } from '@ember/service';
import { getURL } from '../utils/resource';
import { loadCDPAnalytics } from '../utils/analytics';
import uuid from 'v4-uuid';

const FETCH_POLL_INTERVAL_MS = 1000;

export default class CdpService extends Service {
  @service logger;
  @service fastboot;
  @service('aditools-site') siteconfig;
  @service('aditools-analytics') analytics;
  abortController = new AbortController();
  cdpSessionId;

  constructor(owner) {
    super(owner);
    this.cdpSessionId = uuid();
  }

  willDestroy() {
    this.abortController.abort();
  }

  _getFetchPromise(description, url) {
    let reqId = uuid();
    let fastboot = this.fastboot;
    let logger = this.logger;
    if (fastboot.isFastBoot) {
      return Promise.resolve(null);
    } else {
      let startTime = new Date();
      loadCDPAnalytics(
        'Start',
        { sId: this.cdpSessionId, reqId: reqId, name: description, url: url, timeStart: startTime },
        this.analytics,
      );
      let pollHandler = setInterval(() => {
        let endTime = new Date();
        loadCDPAnalytics(
          'In Process',
          {
            sId: this.cdpSessionId,
            reqId: reqId,
            name: description,
            timeStart: startTime,
            timeEnd: endTime,
          },
          this.analytics,
        );
      }, FETCH_POLL_INTERVAL_MS);
      return (this.request = fetch(getURL(url, fastboot, logger), { signal: this.abortController.signal })
        .then((response) => {
          let endTime = new Date();
          if (response.status !== 200) {
            throw { code: response.status, message: response.statusText };
          }
          clearInterval(pollHandler);
          loadCDPAnalytics(
            'End',
            {
              sId: this.cdpSessionId,
              reqId: reqId,
              name: description,
              timeStart: startTime,
              timeEnd: endTime,
              status: { code: response.status, message: response.statusText },
            },
            this.analytics,
          );
          return response.json();
        })
        .catch((error) => {
          clearInterval(pollHandler);
          let endTime = new Date();
          let status = { code: error.code ? error.code : -1, message: error.message ? error.message : 'Other' };
          loadCDPAnalytics(
            'End',
            {
              sId: this.cdpSessionId,
              reqId: reqId,
              name: description,
              timeStart: startTime,
              timeEnd: endTime,
              status: status,
            },
            this.analytics,
          );
          this.logger.error(
            `Load of ${description} at ${url} failed: HTTP Request Failed: ${status.code} ${status.message}`,
          );
          return null;
        }));
    }
  }
  _applyJsonOverrides(item, override, defaultpath, forceunique) {
    const suffix = override && override.startsWith('/testdata') ? 'json' : 'js';
    let url = override ? `${override}/${item}.${suffix}` : `${defaultpath}/${item}.js`;
    return forceunique ? url + '?id=' + new Date().getTime().toString() : url;
  }

  getDeviceModelData(item) {
    return this._getFetchPromise(
      'Model data',
      this._applyJsonOverrides(item, this.siteconfig.ModelUrl, `${this.siteconfig.CdpUrl}/genericsmodelinfo`, false),
    );
  }
  getCmsData(item) {
    return this._getFetchPromise(
      'CMS data',
      this._applyJsonOverrides(
        item,
        this.siteconfig.CmsUrl,
        `${this.siteconfig.CdpUrl}/cms/${this.siteconfig.CdpLocale}`,
        false,
      ),
    );
  }
  getPstIndex(item) {
    let nonRuLocale = this.siteconfig['CdpLocale'] === 'ru' ? 'en' : this.siteconfig['CdpLocale'];
    return this._getFetchPromise(
      'PST index',
      this._applyJsonOverrides(
        item,
        this.siteconfig.PstIndexUrl,
        `${this.siteconfig.CdpUrl}/pst/view/${nonRuLocale}`,
        true,
      ),
    );
  }
  getPstData(item) {
    let nonRuLocale = this.siteconfig['CdpLocale'] === 'ru' ? 'en' : this.siteconfig['CdpLocale'];
    return this._getFetchPromise(
      'PST data',
      this._applyJsonOverrides(
        item,
        this.siteconfig.PstDataUrl,
        `${this.siteconfig.CdpUrl}/pst/data/${nonRuLocale}`,
        true,
      ),
    );
  }
  getPstMapping() {
    return this._getFetchPromise(
      'PST mappings',
      this._applyJsonOverrides(
        'parametermapping',
        this.siteconfig.PstMappingUrl,
        `${this.siteconfig.CdpUrl}/pst`,
        true,
      ),
    );
  }
  getToolsConfig(item) {
    return this._getFetchPromise(
      'tools config',
      this._applyJsonOverrides(item, this.siteconfig.ConfigUrl, `${this.siteconfig.CdpUrl}/toolsconfig`, true),
    );
  }
}
